import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { appWarnings } from 'utils/appWarnings';
import { useNewToast } from 'hooks/newToast';
import { toNumber, parseTaraToNumber } from 'utils/converters';

const RegColheitaManualContext = createContext({});

const success = {
  type: 'success',
};

export function RegColheitaManualProvider({ children }) {
  const { showToast } = useNewToast();

  const [regMdobrasColheita, setRegMdobrasColheita] = useState([]);
  const [regMdobrasColheitaLoading, setRegMdobrasColheitaLoading] =
    useState(false);
  const [regMdobraColheitaLoading, setRegMdobraColheitaLoading] =
    useState(false);
  const [removeRegMdobrasColheitaLoading, setRemoveRegMdobrasColheitaLoading] =
    useState(false);

  const getRegMdobrasColheita = useCallback(async (registroId) => {
    try {
      setRegMdobrasColheitaLoading(true);
      const response = await api
        .regAtividade()
        .getRegMdobrasColheita(registroId);
      setRegMdobrasColheita(response);
      setRegMdobrasColheitaLoading(false);
    } catch (errors) {
      setRegMdobrasColheitaLoading(false);
    }
  }, []);

  const insertRegColheitaManual = useCallback(
    async (registroId, data) => {
      try {
        setRegMdobraColheitaLoading(true);
        const tipoMedicaoId = (item) => {
          const tipoColheita = item?.tipo;
          if (tipoColheita == null) return null;
          return typeof tipoColheita === 'object' ? tipoColheita.id : tipoColheita;
        };
        const isBagItem = (item) => tipoMedicaoId(item) === 'Bag';
        const formattedData = {
          vinculos: data?.map((item) => {
            const colaboradorId = item?.duplicate
              ? item?.glo_vinculo_id
              : item?.colaborador;
            if (isBagItem(item)) {
              const bagCheio = parseTaraToNumber(item?.bag_cheio);
              const bagVazio = parseTaraToNumber(item?.bag_vazio);
              const colhidoCalculado = Math.max(0, bagCheio - bagVazio);
              const colhido =
                item?.colhido != null && item?.colhido !== ''
                  ? parseTaraToNumber(item.colhido)
                  : colhidoCalculado;
              const bagVinculo = {
                tipo: 'Kg',
                medida: toNumber(String(item?.medida ?? 0)),
                valor: toNumber(String(item?.valor ?? 0)),
                colhido,
                resto: 0,
                colaborador: colaboradorId,
                bag_vazio: bagVazio,
                is_bag: 1,
              };
              if (item?.id != null && item?.id !== '') {
                bagVinculo.id = item.id;
              }
              return bagVinculo;
            }
            return {
              ...item,
              resto:
                tipoMedicaoId(item) === 'L'
                  ? toNumber(String(item?.resto ?? 0))
                  : 0,
              tipo: tipoMedicaoId(item),
              medida: toNumber(String(item?.medida ?? 0)),
              valor: toNumber(String(item?.valor ?? 0)),
              colaborador: colaboradorId,
              colhido: toNumber(String(item?.colhido ?? 0)),
              is_bag: 0,
            };
          }),
        };
        await api.regAtividade().insertRegColheita(registroId, formattedData);
        getRegMdobrasColheita(registroId);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setRegMdobraColheitaLoading(false);
      }
    },
    [showToast, getRegMdobrasColheita],
  );

  const removeRegMdobrasColheita = useCallback(
    async (activityId, id, silent = false) => {
      try {
        setRemoveRegMdobrasColheitaLoading(true);
        await api.regAtividade().removeRegMdobrasColheita(activityId, id);
        if (!silent) {
          if (id) {
            showToast({
              ...success,
              message: 'Colaborador excluído com sucesso!',
            });
          } else {
            showToast({
              ...success,
              message: 'Custo de colheita manual excluído com sucesso!',
            });
          }
        }
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setRemoveRegMdobrasColheitaLoading(false);
      }
    },
    [showToast],
  );

  return (
    <RegColheitaManualContext.Provider
      value={useMemo(
        () => ({
          getRegMdobrasColheita,
          regMdobrasColheita,
          regMdobrasColheitaLoading,
          regMdobraColheitaLoading,
          removeRegMdobrasColheita,
          insertRegColheitaManual,
          setRegMdobraColheitaLoading,
          setRegMdobrasColheita,
          removeRegMdobrasColheitaLoading,
        }),
        [
          getRegMdobrasColheita,
          regMdobrasColheita,
          regMdobrasColheitaLoading,
          regMdobraColheitaLoading,
          removeRegMdobrasColheita,
          insertRegColheitaManual,
          setRegMdobraColheitaLoading,
          setRegMdobrasColheita,
          removeRegMdobrasColheitaLoading,
        ],
      )}
    >
      {children}
    </RegColheitaManualContext.Provider>
  );
}

export function useRegColheitaManual() {
  const context = useContext(RegColheitaManualContext);

  if (!context)
    throw new Error('useRegAtividade must be used within an RegAtividades');

  return context;
}
